// src/components/crm/NoteCard.js
// PHASE 1.3 VERIFIER: r2-8b1d4f0c
// PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
//
// Display + action component for a single note in the Notes & Media system.
//
// Round 2 (Phase 1.3) extends the pure-presentation 1.2 component with:
//   - Resolved-flag visual state (muted slate background + grey 'Resolved' chip)
//   - Inline Resolve / Reassign action buttons on active flagged notes
//   - Modal-driven write paths via the resolveNoteFlag / reassignNoteFlag CFs
//
// Phase 1.7 Slice A adds:
//   - Photo grid rendered under the note text (when note.media[] has photos)
//   - Lightbox on thumbnail tap
//   - Empty <p> suppressed when note.text is blank (photo-only notes)
//
// Permission gate (UX-only — defense-in-depth lives in the CF):
//   Buttons render only when current user is one of:
//     - note.authorId
//     - note.flagTargetUserId
//     - high-level role (Admin / Manager / Owner / System Admin variants)
//
// Backwards-compatible: currentUserProfile, orgUsers, and orgId are all
// OPTIONAL props. When absent the component renders exactly as the 1.2
// pure-presentation card. Parents that haven't been updated yet keep working.
//
// Color treatment (priority order):
//   - Flagged + active:           amber background + amber border + amber chip
//   - Flagged + resolved:         slate background + grey flag chip + 'Resolved' chip
//   - Customer-facing (no flag):  blue  background + blue  border + blue  chip
//   - Internal     (no flag):     slate background + slate border + slate chip
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. CF calls go through httpsCallable in the modals — auth wired by SDK.
//      CFs enforce all permission and state-machine checks server-side.
//   2. The frontend permission gate is UX-only. A user who somehow bypassed
//      it would still be rejected at the CF layer.
//   3. All text values render via React's default escaping. No use of
//      dangerouslySetInnerHTML anywhere.
//   4. break-words on the body prevents long unbroken strings from breaking
//      the layout.
//   5. Defensive defaults on every field — a malformed note doc still
//      renders gracefully rather than throwing.
//   6. flagHistory and resolutionNote are intentionally NOT rendered in 1.3
//      — that's the 1.6 triage inbox's job. Audit trail is preserved in
//      Firestore for 1.6 to surface.
//   7. PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a — Photo grid uses
//      getDownloadURL() against thumbnailPath (or storagePath as fallback)
//      gated by storage.rules. Tap-to-lightbox is unscoped (the lightbox
//      is the same component for all photo display paths). The grid only
//      renders entries where storagePath is present so half-written or
//      mid-upload media don't show as broken images.

import React, { useState, useMemo, useEffect } from 'react';
import { Lock, Eye, Flag, FlagOff, CheckCircle2, ImageOff, X, Trash2, ArrowUpRight } from 'lucide-react'; // r1-2e-nc-imports-3f8b7d61 + wppromote-6a3f1e94
import { format } from 'date-fns';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage, db, functions } from '../../firebase/config'; // r1-2e-nc-imports-3f8b7d61
import ResolveFlagModal from './ResolveFlagModal';
import ReassignFlagModal from './ReassignFlagModal';
import UnflagModal from './UnflagModal'; // PHASE 1.6 VERIFIER: r1-16-web-notecard-unflag-16a3f2b8
import NoteMediaLightbox from './NoteMediaLightbox'; // PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
// r1-2e-nc-imports-3f8b7d61
import { httpsCallable } from 'firebase/functions';
import { deleteDoc, doc } from 'firebase/firestore';
import ConfirmationModal from '../common/ConfirmationModal';

// Mirrors functions/index.js HIGH_LEVEL_ROLES list. If you change one, change both.
const HIGH_LEVEL_ROLES = [
  'Admin',
  'Manager',
  'Owner/System Admin',
  'Owner / System Admin',
  'Owner',
  'System Admin',
];

// PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
// How many photo tiles render in the grid before the "+N more" tile.
// Beyond 7 visible + 1 "+more" = 8 total tiles, the lightbox lets users
// see the rest.
const VISIBLE_PHOTO_LIMIT = 7;

// Convert Firestore Timestamp / Date / ISO string to a Date safely.
const toDateSafe = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

// PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
// Single photo tile. Resolves its display URL on mount — prefers thumbnail,
// falls back to storagePath when the thumb hasn't been generated yet (the
// few-second window between upload and Storage trigger completion).
const PhotoTile = ({ mediaItem, onClick, overlayLabel, onDelete }) => { // r1-2e-nc-phototile-3f8b7d61
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);

    const path = mediaItem?.thumbnailPath || mediaItem?.storagePath;
    if (!path) {
      setFailed(true);
      return undefined;
    }

    getDownloadURL(ref(storage, path))
      .then((resolved) => { if (!cancelled) setUrl(resolved); })
      .catch((err) => {
        console.warn('NoteCard PhotoTile: URL resolution failed', err);
        if (!cancelled) setFailed(true);
      });

    return () => { cancelled = true; };
  }, [mediaItem?.thumbnailPath, mediaItem?.storagePath]);

  // r1-2e-nc-phototile-3f8b7d61
  return (
    <div className="relative w-20 h-20 group">
    <button
      type="button"
      onClick={onClick}
      className="relative w-full h-full rounded-md overflow-hidden border border-slate-200 bg-slate-100 hover:opacity-90 transition-opacity"
    >
      {url && !failed && (
        <img src={url} alt="" className="w-full h-full object-cover" />
      )}
      {!url && !failed && (
        <div className="absolute inset-0 bg-slate-200 animate-pulse" />
      )}
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400">
          <ImageOff size={20} />
        </div>
      )}
      {overlayLabel && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <span className="text-white text-sm font-semibold">{overlayLabel}</span>
        </div>
      )}
    </button>
      {/* r1-2e-nc-phototile-3f8b7d61 — X sibling, NOT a child of the tile
          button (a button inside a button is invalid HTML). */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-0.5 right-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-black/60 text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Delete photo"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
};

const NoteCard = ({
  note,
  // The next three are optional. Without them the card renders pure-
  // presentation (no action buttons), preserving 1.2 behaviour. To enable
  // Resolve / Reassign actions on flagged notes, the parent surface must
  // pass all three.
  currentUserProfile,
  orgUsers,
  orgId,
  // wppromote-6a3f1e94 — SEAM 3 (Promote). BOTH optional, and this is the whole
  // reason the button cannot leak onto a screen with nothing to fill: NoteCard
  // renders on SEVEN surfaces — customer form, quote form, quote details,
  // invoice form, invoice details, job form, job quick view — and only the
  // three that own a Work Performed box pass onPromote. The other four pass
  // nothing and render exactly as they did before, which is the same
  // absent-props-means-pure-presentation posture the three props above use.
  //
  // onPromote(note) must APPEND the note's text to that surface's Work Performed
  // draft and must NOT save. promotePending is that surface's unsaved queue.
  onPromote,
  promotePending,
}) => {
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  // PHASE 1.6 VERIFIER: r1-16-web-notecard-unflag-16a3f2b8
  const [showUnflagModal, setShowUnflagModal] = useState(false);
  // PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
  // Lightbox state — null when closed, the photo index when open.
  const [lightboxIndex, setLightboxIndex] = useState(null);
  // r1-2e-nc-handlers-3f8b7d61 — delete state
  const [confirmPhoto, setConfirmPhoto] = useState(null); // pending photo-delete mediaId
  const [confirmNote, setConfirmNote] = useState(false);  // whole-note delete confirm
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Permission gate (UX-only). Defense-in-depth lives in the CF.
  // Hooks must be called unconditionally on every render — the null-guard
  // returns false rather than short-circuiting the hook call. The actual
  // 'render nothing if note is missing' guard lives below, after all hooks.
  const canActOnFlag = useMemo(() => {
    if (!note) return false;
    if (!note.flagged || note.resolved) return false;
    if (!currentUserProfile?.id) return false;
    const isAuthor = note.authorId === currentUserProfile.id;
    // FO-4 multi-reviewer: a member of flagReviewerIds is also a flag target.
    const isFlagTarget = note.flagTargetUserId === currentUserProfile.id
      || (Array.isArray(note.flagReviewerIds) && note.flagReviewerIds.includes(currentUserProfile.id));
    const isHighLevel = HIGH_LEVEL_ROLES.includes(currentUserProfile.role);
    return isAuthor || isFlagTarget || isHighLevel;
  }, [
    note,
    note?.flagged,
    note?.resolved,
    note?.authorId,
    note?.flagTargetUserId,
    currentUserProfile,
  ]);

  // PHASE 1.6 VERIFIER: r1-16-web-notecard-unflag-16a3f2b8
  // Narrower permission than canActOnFlag — the flag TARGET is NOT permitted
  // to unflag. Their grievance flow is reassign ("hand it back / on"), not
  // unflag (destructive: clears flag entirely + deletes notifications).
  // Mirrors the server-side gate in functions/index.js exports.unflagNote.
  const canUnflag = useMemo(() => {
    if (!note) return false;
    if (!note.flagged || note.resolved) return false;
    if (!currentUserProfile?.id) return false;
    const isAuthor = note.authorId === currentUserProfile.id;
    const isHighLevel = HIGH_LEVEL_ROLES.includes(currentUserProfile.role);
    return isAuthor || isHighLevel;
  }, [
    note,
    note?.flagged,
    note?.resolved,
    note?.authorId,
    currentUserProfile,
  ]);

  // PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
  // Filter media[] to entries with a storagePath — half-written items get
  // suppressed gracefully. Only photos in Slice A; video/voice render paths
  // arrive in 1.8/1.9.
  const photos = useMemo(() => {
    if (!note?.media || !Array.isArray(note.media)) return [];
    return note.media.filter(m =>
      m && (m.type === 'photo' || m.type === undefined) && typeof m.storagePath === 'string'
    );
  }, [note?.media]);

  if (!note) return null;

  const flagged = !!note.flagged;
  const resolved = !!note.resolved;
  const isCustomerFacing = note.visibility === 'customer';
  const noteDate = toDateSafe(note.addedAt);
  const resolvedDate = toDateSafe(note.resolvedAt);
  // PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
  // Suppress the <p> tag for photo-only notes so there's no empty vertical
  // gap between the chip row and the photo grid.
  const hasText = typeof note.text === 'string' && note.text.trim().length > 0;

  // Need orgId to call any CF. Need orgUsers (array) for the reassign picker.
  const buttonsAvailable = canActOnFlag && !!orgId;
  const reassignAvailable = buttonsAvailable && Array.isArray(orgUsers);
  // PHASE 1.6 VERIFIER: r1-16-web-notecard-unflag-16a3f2b8
  const unflagAvailable = canUnflag && !!orgId;

  // Container colour: flagged-active = amber; flagged-resolved = slate (muted);
  // customer-facing = blue; internal = slate.
  const containerClasses = flagged && !resolved
    ? 'bg-amber-50 border-amber-200'
    : isCustomerFacing
      ? 'bg-blue-50 border-blue-100'
      : 'bg-slate-50 border-slate-100';

  // Flag chip colour: amber if active, slate if resolved.
  const flagChipClasses = resolved
    ? 'text-slate-500 bg-slate-200'
    : 'text-amber-700 bg-amber-100';

  // PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
  // Photo grid: first VISIBLE_PHOTO_LIMIT tiles render normally. If there
  // are more, the 8th tile becomes "+N more" and opens the lightbox at the
  // 8th photo.
  const hasPhotos = photos.length > 0;
  const visiblePhotos = photos.slice(0, VISIBLE_PHOTO_LIMIT);
  const overflowCount = Math.max(0, photos.length - VISIBLE_PHOTO_LIMIT);

  // r1-2e-nc-handlers-3f8b7d61
  // Show delete controls only to the note author or a high-level role, and
  // only when the surface supplied orgId. Mirrors firestore.rules + the
  // deleteNotePhoto CF gate. Absent props => no controls (pure-presentation).
  // Reuses the existing module-level HIGH_LEVEL_ROLES and the existing hasText.
  const canDelete = !!orgId
    && !!currentUserProfile?.id
    && (note.authorId === currentUserProfile.id || HIGH_LEVEL_ROLES.includes(currentUserProfile.role));

  // wppromote-6a3f1e94 — Promote shows only when the surface supplied a handler
  // (so only where there is a Work Performed box to fill), the note is INTERNAL
  // (a customer-facing note already reaches the invoice BENEATH Work Performed,
  // so promoting it would print the same sentence to the customer twice), it has
  // text to copy (a photo-only note has nothing), and it has not already been
  // promoted or queued in this editing session.
  const promoteAvailable = typeof onPromote === 'function'
    && !isCustomerFacing
    && hasText
    && !note.promotedToWorkPerformed
    && !promotePending;

  // The marker chip. `promotePending` is this session's UNSAVED queue, so it says
  // so — the tag only becomes permanent when the box itself is saved. The hover
  // title names WHICH Work Performed it went into: the note doc is shared between
  // a job and the invoice made from it, and after Seam 2 the invoice is its own
  // document, so "the job's" and "the invoice's" are genuinely different answers.
  const promotedDate = toDateSafe(note.promotedAt);
  const promotedTitle = note.promotedToWorkPerformed
    ? `Added to the ${note.promotedTargetType === 'invoice' ? 'invoice' : 'job'}'s Work Performed`
      + `${note.promotedByName ? ` by ${note.promotedByName}` : ''}`
      + `${promotedDate ? ` · ${format(promotedDate, 'MMM d, yyyy')}` : ''}`
    : undefined;

  // Deleting THIS photo removes the whole note when it's the only photo and
  // the note has no text (the server enforces the same rule).
  const photoDeleteRemovesNote = (mediaId) =>
    !hasText && photos.length === 1 && photos[0]?.mediaId === mediaId;

  const runDeletePhoto = async (mediaId) => {
    if (!mediaId || deleteBusy) return;
    setDeleteBusy(true);
    try {
      // orgId is derived server-side; the client sends only noteId + mediaId.
      const fn = httpsCallable(functions, 'deleteNotePhoto');
      await fn({ noteId: note.id, mediaId });
    } catch (err) {
      console.warn('NoteCard: deleteNotePhoto failed', err);
      window.alert('Sorry — that photo could not be deleted. Please try again.');
    } finally {
      setDeleteBusy(false);
      setConfirmPhoto(null);
    }
  };

  const runDeleteNote = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deleteDoc(doc(db, `organizations/${orgId}/notes/${note.id}`));
    } catch (err) {
      console.warn('NoteCard: note delete failed', err);
      window.alert('Sorry — that note could not be deleted. Please try again.');
    } finally {
      setDeleteBusy(false);
      setConfirmNote(false);
    }
  };

  return (
    <>
      <div className={`rounded-md p-2.5 text-sm border ${containerClasses}`}>
        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
          {isCustomerFacing ? (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
              <Eye size={9} /> Customer-facing
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded-full">
              <Lock size={9} /> Internal
            </span>
          )}
          {flagged && (
            <span className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${flagChipClasses}`}>
              <Flag size={9} /> Flagged · {note.flagTargetName || 'Office'}
            </span>
          )}
          {flagged && resolved && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded-full">
              <CheckCircle2 size={9} /> Resolved
              {note.resolvedByName ? ` · ${note.resolvedByName}` : ''}
              {resolvedDate ? ` · ${format(resolvedDate, 'MMM d')}` : ''}
            </span>
          )}
          {/* wppromote-6a3f1e94 — green once saved, amber while it is only queued. */}
          {(note.promotedToWorkPerformed || promotePending) && (
            <span
              title={promotedTitle}
              className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                note.promotedToWorkPerformed
                  ? 'text-emerald-700 bg-emerald-100'
                  : 'text-amber-700 bg-amber-100'
              }`}
            >
              <ArrowUpRight size={9} />
              {note.promotedToWorkPerformed
                ? 'Added to Work Performed'
                : 'Added to Work Performed — not saved yet'}
            </span>
          )}
          <span className="text-[10px] text-slate-400">
            {note.authorName || 'Unknown'}
            {noteDate ? ` · ${format(noteDate, 'MMM d, h:mm a')}` : ''}
          </span>
          {/* r1-2e-nc-notedelete-3f8b7d61 — whole-note delete (author/high-level) */}
          {canDelete && (
            <button
              type="button"
              onClick={() => setConfirmNote(true)}
              disabled={deleteBusy}
              className="ml-auto flex items-center justify-center w-6 h-6 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
              title="Delete note"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>

        {/* PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
            Text only rendered when there's actual text — empty <p> on
            photo-only notes leaves a confusing blank line. */}
        {hasText && (
          <p className="text-slate-700 whitespace-pre-line break-words">
            {note.text}
          </p>
        )}

        {/* PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a
            Photo grid — only renders when note has photos. */}
        {hasPhotos && (
          <div className={`flex flex-wrap gap-1.5 ${hasText ? 'mt-2' : ''}`}>
            {visiblePhotos.map((m, idx) => (
              <PhotoTile
                key={m.mediaId || idx}
                mediaItem={m}
                onClick={() => setLightboxIndex(idx)}
                onDelete={canDelete ? () => setConfirmPhoto(m.mediaId) : undefined}
              />
            ))}
            {overflowCount > 0 && (
              <PhotoTile
                mediaItem={photos[VISIBLE_PHOTO_LIMIT]}
                onClick={() => setLightboxIndex(VISIBLE_PHOTO_LIMIT)}
                overlayLabel={`+${overflowCount}`}
              />
            )}
          </div>
        )}

        {/* Action row. wppromote-6a3f1e94 — WAS `buttonsAvailable &&` alone (the
            flag actions, which exist only on active flagged notes). Promote has
            nothing to do with flagging, so the row now renders when EITHER is
            available and each group guards itself. */}
        {(buttonsAvailable || promoteAvailable) && (
          <div className="flex items-center justify-end gap-2 mt-2 flex-wrap">
            {/* wppromote-6a3f1e94 — appends, never saves. Sits leftmost so the
                destructive/state-changing flag actions keep their position. */}
            {promoteAvailable && (
              <button
                type="button"
                onClick={() => onPromote(note)}
                className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 transition-colors"
                title="Copy this note's text into Work Performed, so you can edit it before saving"
              >
                <ArrowUpRight size={12} /> Add to Work Performed
              </button>
            )}
            {buttonsAvailable && (<>
            {/* PHASE 1.6 VERIFIER: r1-16-web-notecard-unflag-16a3f2b8
                Unflag is destructive (clears flag + deletes notifications), so
                styled distinctly from Resolve (primary amber) and Reassign
                (outlined slate). Text-only red signals "different category of
                action — be intentional." Permission gate is narrower than
                Resolve/Reassign (author OR high-level; flag target excluded). */}
            {unflagAvailable && (
              <button
                type="button"
                onClick={() => setShowUnflagModal(true)}
                className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
              >
                <FlagOff size={12} /> Unflag
              </button>
            )}
            {reassignAvailable && (
              <button
                type="button"
                onClick={() => setShowReassignModal(true)}
                className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-400 transition-colors"
              >
                Reassign
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowResolveModal(true)}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white bg-amber-500 hover:bg-amber-600 transition-colors"
            >
              <CheckCircle2 size={12} /> Resolve
            </button>
            </>)}{/* wppromote-6a3f1e94 — closes the buttonsAvailable group */}
          </div>
        )}
      </div>

      {showResolveModal && (
        <ResolveFlagModal
          note={note}
          orgId={orgId}
          onClose={() => setShowResolveModal(false)}
        />
      )}
      {showReassignModal && (
        <ReassignFlagModal
          note={note}
          orgId={orgId}
          currentUserProfile={currentUserProfile}
          orgUsers={orgUsers}
          onClose={() => setShowReassignModal(false)}
        />
      )}
      {/* PHASE 1.6 VERIFIER: r1-16-web-notecard-unflag-16a3f2b8 */}
      {showUnflagModal && (
        <UnflagModal
          note={note}
          orgId={orgId}
          onClose={() => setShowUnflagModal(false)}
        />
      )}
      {/* PHASE 1.7 VERIFIER: r1-17a-web-nc-3c8f4e2a */}
      {lightboxIndex !== null && (
        <NoteMediaLightbox
          media={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
      {/* r1-2e-nc-confirm-3f8b7d61 */}
      <ConfirmationModal
        isOpen={confirmPhoto !== null}
        onClose={() => setConfirmPhoto(null)}
        onConfirm={() => runDeletePhoto(confirmPhoto)}
        title="Delete photo?"
        message={
          photoDeleteRemovesNote(confirmPhoto)
            ? 'This is the only photo and the note has no text, so deleting it will delete the whole note. This can’t be undone.'
            : 'This photo will be permanently deleted. This can’t be undone.'
        }
        confirmText={photoDeleteRemovesNote(confirmPhoto) ? 'Delete note' : 'Yes, Delete'}
      />
      <ConfirmationModal
        isOpen={confirmNote}
        onClose={() => setConfirmNote(false)}
        onConfirm={runDeleteNote}
        title="Delete note?"
        message={
          photos.length > 0
            ? `This note and its ${photos.length} photo${photos.length === 1 ? '' : 's'} will be permanently deleted. This can’t be undone.`
            : 'This note will be permanently deleted. This can’t be undone.'
        }
        confirmText="Yes, Delete"
      />
    </>
  );
};

export default NoteCard;